import { computed, inject, Service, signal, TemplateRef, WritableSignal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, ActivatedRouteSnapshot, NavigationEnd, Params, Route, Router, UrlTree } from '@angular/router';
import { filter } from 'rxjs';
import {
  buildAbsoluteCommands,
  buildDetailDrawerTrail,
  DetailDrawerTrailEntry,
  findDetailDrawerLevelRoutes,
} from './build-detail-drawer-trail.function';
import {
  DETAIL_DRAWER_ANCHOR_PARAMETER,
  DETAIL_DRAWER_LEVEL_DATA_KEY,
  DETAIL_DRAWER_TAB_PARAMETER,
  DetailDrawerItemTarget,
  DetailDrawerLevel,
  DetailDrawerPageTrail,
  DetailDrawerPathStep,
} from './detail-drawer.model';

/**
 * The seam between the detail drawer shell and the item rendered inside it.
 *
 * Provided by the shell, so every item below it injects the same instance. The item hands over
 * what belongs in the drawer's header and footer through {@link DetailDrawerActionsDirective},
 * {@link DetailDrawerHeadingDirective}, {@link DetailDrawerTabsDirective} and
 * {@link DetailDrawerFooterDirective}, names its own hierarchy through {@link setTrail} and its
 * title through {@link nameCurrentItem}, steps to another item of its kind through
 * {@link goToSibling}, links to another item inside the drawer through {@link itemUrl} — or
 * {@link itemPathUrl}, more than a level below — and asks the drawer to close through {@link close}.
 */
@Service({ autoProvided: false })
export class DetailDrawerFrame {
  private readonly router = inject(Router);
  /** The route the shell stands on, which the path of every level is read from. */
  private readonly shellRoute = inject(ActivatedRoute);

  private readonly openState = signal<boolean>(true);
  private readonly headerFoldedState = signal<boolean>(false);
  private readonly actionsTemplate = signal<TemplateRef<unknown> | null>(null);
  private readonly headingTemplate = signal<TemplateRef<unknown> | null>(null);
  private readonly tabsTemplate = signal<TemplateRef<unknown> | null>(null);
  private readonly footerTemplate = signal<TemplateRef<unknown> | null>(null);
  private readonly pageTrail = signal<DetailDrawerPageTrail | null>(null);
  private readonly trailPending = signal<boolean>(false);
  /**
   * The title each item gave itself while it was on show, by kind and id. Kept as long as the
   * drawer, so a crumb above the item on show names its item: the levels above are not rendered,
   * so they cannot name themselves again.
   */
  private readonly itemTitles = signal<ReadonlyMap<string, string>>(new Map());
  /** Emits once every navigation has finished, which is when the route tree names the new path. */
  private readonly navigationEnd = toSignal(
    this.router.events.pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd)),
  );

  /** False once the drawer was asked to close; it navigates away when its exit animation ends. */
  public readonly isOpen = this.openState.asReadonly();
  /**
   * Whether the header has folded its heading away while the content is scrolled. The controls
   * stay in view, so an item can show there what its heading said.
   */
  public readonly isHeaderFolded = this.headerFoldedState.asReadonly();
  /** The item's controls, shown beside the trail in front of the close button. */
  public readonly actions = this.actionsTemplate.asReadonly();
  /** The item's title block, shown below the trail. */
  public readonly heading = this.headingTemplate.asReadonly();
  /** The item's tab list, shown at the foot of the header; the header has no border while this is set. */
  public readonly tabs = this.tabsTemplate.asReadonly();
  /** The item's footer, pinned below its content; the drawer has none while this is null. */
  public readonly footer = this.footerTemplate.asReadonly();
  /** The item's own hierarchy, in place of its level's crumb; null while the level names itself. */
  public readonly trail = this.pageTrail.asReadonly();
  /** Whether the item has yet to say which crumbs it stands under; its own crumb is held back until it does. */
  public readonly isTrailPending = this.trailPending.asReadonly();
  /** Whether the item on show was opened from another one, rather than being the drawer's first level. */
  public readonly isNested = computed<boolean>(() => {
    this.navigationEnd();
    return buildDetailDrawerTrail(this.shellRoute.snapshot).length > 1;
  });

  /** Starts the drawer's exit animation; closing leaves the whole path, back to the host page. */
  public close(): void {
    this.openState.set(false);
  }

  /**
   * Follows the drawer's own report; the shell calls it.
   *
   * @param {boolean} folded - Whether the header is now folded
   */
  public setHeaderFolded(folded: boolean): void {
    this.headerFoldedState.set(folded);
  }

  /**
   * @param {TemplateRef<unknown>} template - The item's controls
   */
  public showActions(template: TemplateRef<unknown>): void {
    this.actionsTemplate.set(template);
  }

  /**
   * Clears the controls, unless another item has already put its own in their place.
   *
   * @param {TemplateRef<unknown>} template - The controls being removed
   */
  public clearActions(template: TemplateRef<unknown>): void {
    this.clearIfShown(this.actionsTemplate, template);
  }

  /**
   * @param {TemplateRef<unknown>} template - The item's title block
   */
  public showHeading(template: TemplateRef<unknown>): void {
    this.headingTemplate.set(template);
  }

  /**
   * Clears the title block, unless another item has already put its own in its place.
   *
   * @param {TemplateRef<unknown>} template - The title block being removed
   */
  public clearHeading(template: TemplateRef<unknown>): void {
    this.clearIfShown(this.headingTemplate, template);
  }

  /**
   * @param {TemplateRef<unknown>} template - The item's tab list
   */
  public showTabs(template: TemplateRef<unknown>): void {
    this.tabsTemplate.set(template);
  }

  /**
   * Clears the tab list, unless another item has already put its own in its place.
   *
   * @param {TemplateRef<unknown>} template - The tab list being removed
   */
  public clearTabs(template: TemplateRef<unknown>): void {
    this.clearIfShown(this.tabsTemplate, template);
  }

  /**
   * @param {TemplateRef<unknown>} template - The item's footer
   */
  public showFooter(template: TemplateRef<unknown>): void {
    this.footerTemplate.set(template);
  }

  /**
   * Clears the footer, unless another item has already put its own in its place.
   *
   * @param {TemplateRef<unknown>} template - The footer being removed
   */
  public clearFooter(template: TemplateRef<unknown>): void {
    this.clearIfShown(this.footerTemplate, template);
  }

  /**
   * Names the item's own hierarchy within its level. `null` says the item cannot tell yet — its
   * data has not arrived — and keeps whatever it last named, so the trail never shows a crumb it
   * has to take back; before the first answer, the item's own crumb is held back.
   *
   * @param {DetailDrawerPageTrail | null} trail - The item's crumbs, or null while unknown
   */
  public setTrail(trail: DetailDrawerPageTrail | null): void {
    if (trail) {
      this.pageTrail.set(trail);
      this.trailPending.set(false);
      return;
    }
    if (!this.pageTrail()) {
      this.trailPending.set(true);
    }
  }

  /**
   * Gives the item on show the title its crumb reads once an item it contains is on show below
   * it. Its own crumb reads its kind meanwhile: the item's title is on the page right beneath.
   *
   * @param {string} title - The item's title, as its heading reads
   */
  public nameCurrentItem(title: string): void {
    const trail = buildDetailDrawerTrail(this.shellRoute.snapshot);
    const current = trail[trail.length - 1];
    if (!current) {
      return;
    }
    const itemKey = buildItemKey(current);
    if (this.itemTitles().get(itemKey) === title) {
      return;
    }
    this.itemTitles.update((titles) => new Map(titles).set(itemKey, title));
  }

  /**
   * @param {DetailDrawerTrailEntry} entry - A level of the drawer's path
   * @returns {string | undefined} The title its item gave itself; undefined where the item has not
   *   been on show since the drawer opened, as on a reload
   */
  public findItemTitle(entry: DetailDrawerTrailEntry): string | undefined {
    return this.itemTitles().get(buildItemKey(entry));
  }

  /** Hands the crumb back to the level, as it is once the item that named its own has gone. */
  public clearTrail(): void {
    this.pageTrail.set(null);
    this.trailPending.set(false);
  }

  /**
   * The address of another item on the level on show — the next Aufgabe, an Unteraufgabe, the
   * Aufgabe above one. The item on show leaves its matrix parameters behind: another item starts
   * on its own first tab.
   *
   * @param {number | string} id - The item to lead to
   * @returns {UrlTree} Its address, keeping the query string
   */
  public siblingUrl(id: number | string): UrlTree {
    const trail = buildDetailDrawerTrail(this.shellRoute.snapshot);
    const commands = [...(trail[trail.length - 1]?.commands ?? [])];
    if (typeof commands[commands.length - 1] === 'object') {
      commands.pop();
    }
    commands[commands.length - 1] = String(id);

    return this.router.createUrlTree(commands, { queryParamsHandling: 'preserve' });
  }

  /**
   * Shows another item of the level on show in place of this one. The panel stays put and the
   * browser gains a history entry.
   *
   * @param {number | string} id - The item to show
   * @returns {Promise<boolean>} Whether the navigation went through
   */
  public goToSibling(id: number | string): Promise<boolean> {
    return this.router.navigateByUrl(this.siblingUrl(id));
  }

  /**
   * The address of an item inside this drawer, as a link from the item on show reaches it:
   *
   * - back up to the level that already shows it, on the tab it was left on — an Aufgabe opened
   *   from its Anforderung links back to that Anforderung rather than opening it again;
   * - a level below, where the item on show contains its kind — an Anforderung's Aufgabe, so the
   *   trail reads «Anforderung › Aufgabe» — or stacks it, as an Anforderung's Dokument opens in a
   *   drawer over it; the item on show keeps its matrix parameters, so going back up returns to
   *   the tab it was left on;
   * - otherwise the drawer's first level, in place of the whole path — the Anforderung an Aufgabe
   *   was raised against, which starts a trail of its own.
   *
   * Every one is a navigation, so the browser's back button returns to the item on show.
   *
   * A target opens the item on a tab, and at a place within it, whichever of the three it is —
   * back up to an item already shown, that tab replaces the one it was left on.
   *
   * @param {string} type - The kind to lead to
   * @param {number | string} id - The item to lead to
   * @param {DetailDrawerItemTarget} [target] - The tab and the place within it to open the item on
   * @returns {UrlTree | null} Its address, keeping the query string; null where the drawer shows no such kind
   */
  public itemUrl(type: string, id: number | string, target?: DetailDrawerItemTarget): UrlTree | null {
    const ancestor = buildDetailDrawerTrail(this.shellRoute.snapshot)
      .slice(0, -1)
      .find((entry) => entry.level.type === type && findTrailEntryId(entry) === String(id));
    if (ancestor) {
      return this.router.createUrlTree(applyItemTarget([...ancestor.commands], target), { queryParamsHandling: 'preserve' });
    }

    const levelSnapshot = findCurrentLevelSnapshot(this.shellRoute.snapshot);
    const levelBelow = findLevelBelow(levelSnapshot?.routeConfig, type);
    if (levelSnapshot && levelBelow?.path) {
      return this.router.createUrlTree(
        applyItemTarget([...buildAbsoluteCommands(levelSnapshot), ...buildRouteCommands(levelBelow.path, id)], target),
        { queryParamsHandling: 'preserve' },
      );
    }

    const firstLevel = findLevelRoute(this.shellRoute.snapshot.routeConfig?.children, type);
    if (!firstLevel?.path) {
      return null;
    }
    return this.router.createUrlTree(
      applyItemTarget([...buildAbsoluteCommands(this.shellRoute.snapshot), ...buildRouteCommands(firstLevel.path, id)], target),
      { queryParamsHandling: 'preserve' },
    );
  }

  /**
   * The address of an item more than one level below the item on show, through the items between
   * them — the Aufgabe of an Erlass's Anforderung, so the trail reads «Erlass › Anforderung ›
   * Aufgabe». Each step is a kind the level before it contains; a step naming a tab opens its item
   * there, and its crumb returns to it. The item on show keeps its matrix parameters.
   *
   * @param {readonly DetailDrawerPathStep[]} steps - The items on the way, the last one the item to lead to
   * @returns {UrlTree | null} Its address, keeping the query string; null where the drawer holds no such path
   */
  public itemPathUrl(steps: readonly DetailDrawerPathStep[]): UrlTree | null {
    const levelSnapshot = findCurrentLevelSnapshot(this.shellRoute.snapshot);
    if (!levelSnapshot || !steps.length) {
      return null;
    }

    const commands = buildAbsoluteCommands(levelSnapshot);
    let levelRoute: Route | null | undefined = levelSnapshot.routeConfig;
    for (const step of steps) {
      const levelBelow = findLevelBelow(levelRoute, step.type);
      if (!levelBelow?.path) {
        return null;
      }
      commands.push(...buildRouteCommands(levelBelow.path, step.id));
      if (step.tab) {
        commands.push({ [DETAIL_DRAWER_TAB_PARAMETER]: step.tab });
      }
      levelRoute = levelBelow;
    }

    return this.router.createUrlTree(commands, { queryParamsHandling: 'preserve' });
  }

  private clearIfShown(slot: WritableSignal<TemplateRef<unknown> | null>, template: TemplateRef<unknown>): void {
    if (slot() === template) {
      slot.set(null);
    }
  }
}

/**
 * @param {ActivatedRouteSnapshot} shellSnapshot - The route the drawer shell stands on
 * @returns {ActivatedRouteSnapshot | null} The deepest route that is a level of the drawer's path, whose config holds the levels below it
 */
function findCurrentLevelSnapshot(shellSnapshot: ActivatedRouteSnapshot): ActivatedRouteSnapshot | null {
  const levelRoutes = findDetailDrawerLevelRoutes(shellSnapshot);
  return levelRoutes[levelRoutes.length - 1]?.snapshot ?? null;
}

/**
 * @param {Route | null | undefined} levelRoute - The config of the level on show
 * @param {string} type - The kind of item
 * @returns {Route | undefined} The level below it that shows that kind: one it contains, beside
 *   its item, or one it stacks, below the item — both on the same address
 */
function findLevelBelow(levelRoute: Route | null | undefined, type: string): Route | undefined {
  const itemRoute = levelRoute?.children?.find((child) => child.path === '');
  return findLevelRoute(levelRoute?.children, type) ?? findLevelRoute(itemRoute?.children, type);
}

/**
 * @param {Route[] | undefined} routes - The routes to look through
 * @param {string} type - The kind of item
 * @returns {Route | undefined} The level among them that shows that kind
 */
function findLevelRoute(routes: Route[] | undefined, type: string): Route | undefined {
  return routes?.find((route) => (route.data?.[DETAIL_DRAWER_LEVEL_DATA_KEY] as DetailDrawerLevel | undefined)?.type === type);
}

/**
 * @param {(string | Params)[]} commands - Router commands ending in an item's segment, or in its matrix parameters
 * @param {DetailDrawerItemTarget | undefined} target - The tab and the place within it to open the item on
 * @returns {(string | Params)[]} The same commands, with the target in place of any tab and place they
 *   named; unchanged without a target
 */
function applyItemTarget(commands: (string | Params)[], target: DetailDrawerItemTarget | undefined): (string | Params)[] {
  if (!target) {
    return commands;
  }

  const last = commands[commands.length - 1];
  const hasParameters = typeof last === 'object';
  const parameters: Params = hasParameters ? { ...last } : {};
  delete parameters[DETAIL_DRAWER_TAB_PARAMETER];
  delete parameters[DETAIL_DRAWER_ANCHOR_PARAMETER];
  if (target.tab) {
    parameters[DETAIL_DRAWER_TAB_PARAMETER] = target.tab;
  }
  if (target.anchor) {
    parameters[DETAIL_DRAWER_ANCHOR_PARAMETER] = target.anchor;
  }

  const segments = hasParameters ? commands.slice(0, -1) : commands;
  return Object.keys(parameters).length ? [...segments, parameters] : segments;
}

/**
 * @param {string} path - A level's route path, such as `aufgaben/:id`
 * @param {number | string} id - The item to fill in
 * @returns {string[]} The router commands for it
 */
function buildRouteCommands(path: string, id: number | string): string[] {
  return path.split('/').map((part) => (part.startsWith(':') ? String(id) : part));
}

/**
 * @param {DetailDrawerTrailEntry} entry - A level of the drawer's path
 * @returns {string} What identifies the item it shows across the drawer: its kind and its id
 */
function buildItemKey(entry: DetailDrawerTrailEntry): string {
  return `${entry.level.type}:${findTrailEntryId(entry) ?? ''}`;
}

/**
 * @param {DetailDrawerTrailEntry} entry - A level of the drawer's path
 * @returns {string | undefined} The id of the item it shows, which is its last path segment
 */
function findTrailEntryId(entry: DetailDrawerTrailEntry): string | undefined {
  return [...entry.commands].reverse().find((command): command is string => typeof command === 'string');
}
